/**
 * Appeal category helpers
 *
 * Labels and evidence guidance for the procedure and denial categories an appeal is configured
 * with. The categories come from the appeal-create form; nothing here reads remit data.
 */

import {
  DenialCategory,
  ProcedureCategory,
  PayerTemplateId,
} from '../../constants/appealTemplates';

/**
 * Get human-readable label for procedure category
 */
export function getProcedureCategoryLabel(category: ProcedureCategory): string {
  const labels: Record<ProcedureCategory, string> = {
    Spine: 'Spine Surgery',
    Nerve: 'Nerve/Carpal/Cubital Tunnel',
    Arthroscopy: 'Arthroscopy',
    General: 'General Procedure',
  };
  return labels[category];
}

/**
 * Determine if post-op course section is likely needed
 */
export function isPostOpCourseNeeded(
  denialCategory: DenialCategory,
  payerTemplateId: PayerTemplateId
): boolean {
  // Always needed for site-of-care disputes
  if (denialCategory === 'SiteOfCare') return true;

  // UHC is particularly stringent about post-op justification
  if (payerTemplateId === 'UHC_OPTUM' && denialCategory === 'Administrative') return true;

  return false;
}

/**
 * Get evidence priorities based on procedure category
 */
export function getEvidencePriorities(procedureCategory: ProcedureCategory): string[] {
  switch (procedureCategory) {
    case 'Spine':
      return [
        'Imaging studies (MRI, CT, X-ray)',
        'Neurologic examination findings',
        'Functional impairment documentation',
        'Failed conservative treatment history',
        'Post-operative course details',
      ];
    case 'Nerve':
      return [
        'EMG/NCS results',
        'Physical exam findings (Tinel, Phalen)',
        'Work/functional interference',
        'Failed bracing/splinting',
        'Failed injection therapy',
      ];
    case 'Arthroscopy':
      return [
        'MRI findings',
        'Physical exam findings',
        'Functional limitations',
        'Failed physical therapy',
        'Injection history',
      ];
    default:
      return [
        'Diagnostic findings',
        'Physical examination',
        'Conservative treatment history',
        'Functional impact',
        'Clinical necessity documentation',
      ];
  }
}
